import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Trash2,
  Type,
  Image,
  Video,
  Heading1,
  Heading2,
  Heading3,
  MoveUp,
  MoveDown,
} from "lucide-react";
import { Button } from "../ui/button";
import ImageUpload from "../common/ImageUpload";

export type ContentBlockType =
  | "paragraph"
  | "heading1"
  | "heading2"
  | "heading3"
  | "image"
  | "video";

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  content: string;
  alt?: string; // For images
  caption?: string; // For images/videos
  videoUrl?: string; // For videos
  videoSource?: string; // For videos (youtube, vimeo, etc.)
}

interface RichTextEditorProps {
  value: string; // HTML string
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

// Convert HTML to blocks
const htmlToBlocks = (html: string): ContentBlock[] => {
  if (!html || html.trim() === "") {
    return [{ id: generateId(), type: "paragraph", content: "" }];
  }

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const blocks: ContentBlock[] = [];
  const elements = Array.from(tempDiv.children);

  if (elements.length === 0) {
    // If no block elements, treat the whole content as a paragraph
    const text = tempDiv.textContent || "";
    if (text.trim()) {
      blocks.push({ id: generateId(), type: "paragraph", content: text });
    } else {
      blocks.push({ id: generateId(), type: "paragraph", content: "" });
    }
    return blocks;
  }

  elements.forEach((el) => {
    const tagName = el.tagName.toLowerCase();
    const text = el.textContent || "";
    const img = el.querySelector("img");
    const iframe = el.querySelector("iframe");
    const video = el.querySelector("video");

    if (img) {
      blocks.push({
        id: generateId(),
        type: "image",
        content: img.src,
        alt: img.alt || "",
        caption: text || "",
      });
    } else if (iframe || video) {
      const src =
        iframe?.getAttribute("src") || video?.getAttribute("src") || "";
      blocks.push({
        id: generateId(),
        type: "video",
        content: "",
        videoUrl: src,
        videoSource: src.includes("youtube")
          ? "youtube"
          : src.includes("vimeo")
          ? "vimeo"
          : "other",
        caption: text || "",
      });
    } else if (tagName === "h1") {
      blocks.push({ id: generateId(), type: "heading1", content: text });
    } else if (tagName === "h2") {
      blocks.push({ id: generateId(), type: "heading2", content: text });
    } else if (tagName === "h3") {
      blocks.push({ id: generateId(), type: "heading3", content: text });
    } else {
      blocks.push({ id: generateId(), type: "paragraph", content: text });
    }
  });

  return blocks.length > 0
    ? blocks
    : [{ id: generateId(), type: "paragraph", content: "" }];
};

// Convert blocks to HTML
const blocksToHtml = (blocks: ContentBlock[]): string => {
  return blocks
    .map((block) => {
      switch (block.type) {
        case "heading1":
          return `<h1>${escapeHtml(block.content)}</h1>`;
        case "heading2":
          return `<h2>${escapeHtml(block.content)}</h2>`;
        case "heading3":
          return `<h3>${escapeHtml(block.content)}</h3>`;
        case "image":
          return `<figure><img src="${block.content}" alt="${escapeHtml(
            block.alt || ""
          )}" /><figcaption>${escapeHtml(
            block.caption || ""
          )}</figcaption></figure>`;
        case "video":
          if (block.videoUrl) {
            if (block.videoSource === "youtube") {
              const videoId = extractYouTubeId(block.videoUrl);
              return `<figure><iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe><figcaption>${escapeHtml(
                block.caption || ""
              )}</figcaption></figure>`;
            } else if (block.videoSource === "vimeo") {
              const videoId = extractVimeoId(block.videoUrl);
              return `<figure><iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allowfullscreen></iframe><figcaption>${escapeHtml(
                block.caption || ""
              )}</figcaption></figure>`;
            } else {
              return `<figure><video src="${
                block.videoUrl
              }" controls></video><figcaption>${escapeHtml(
                block.caption || ""
              )}</figcaption></figure>`;
            }
          }
          return "";
        case "paragraph":
        default:
          return `<p>${escapeHtml(block.content)}</p>`;
      }
    })
    .join("");
};

const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

const generateId = (): string => {
  return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const extractYouTubeId = (url: string): string => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : "";
};

const extractVimeoId = (url: string): string => {
  const regExp =
    /^.*(vimeo.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/)|(video\/|))(\d+)(|\/|\?.*)$/;
  const match = url.match(regExp);
  return match ? match[6] : "";
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start writing...",
  className = "",
}) => {
  // Helper function to normalize HTML for comparison (only for comparison, not for output)
  const normalizeHtmlForComparison = (html: string): string => {
    if (!html) return "";
    // Normalize whitespace between tags for comparison
    // This helps catch cases where HTML is functionally equivalent but formatted differently
    return html
      .replace(/>\s+</g, "><") // Remove whitespace between tags
      .trim();
  };

  const initialValue = value || "";
  const [blocks, setBlocks] = useState<ContentBlock[]>(() =>
    htmlToBlocks(initialValue)
  );

  // Refs to track state and prevent infinite loops
  const lastValueRef = React.useRef<string>(
    normalizeHtmlForComparison(initialValue)
  );
  const isInternalUpdateRef = React.useRef<boolean>(false);
  const onChangeRef = React.useRef(onChange);
  const blocksRef = React.useRef(blocks);
  const isMountedRef = React.useRef<boolean>(false);

  // Keep refs up to date
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  // Mark component as mounted after initial render
  React.useEffect(() => {
    isMountedRef.current = true;
  }, []);

  // Update blocks when value prop changes from external source
  React.useEffect(() => {
    // Skip on initial mount to avoid unnecessary updates
    if (!isMountedRef.current) {
      lastValueRef.current = normalizeHtmlForComparison(value || "");
      return;
    }

    const currentValue = normalizeHtmlForComparison(value || "");
    const lastValue = lastValueRef.current;

    // Only process if value actually changed
    if (currentValue === lastValue) {
      return;
    }

    // Generate HTML from current blocks
    const currentHtml = normalizeHtmlForComparison(
      blocksToHtml(blocksRef.current)
    );

    // If the incoming value is different from what we generated, it's an external update
    if (currentValue !== currentHtml) {
      isInternalUpdateRef.current = true;
      const newBlocks = htmlToBlocks(value || "");
      setBlocks(newBlocks);
      lastValueRef.current = currentValue;

      // Reset flag after React processes the update
      // Use setTimeout to ensure it happens after the state update is processed
      setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 0);
    } else {
      // Value matches our HTML, so just update the ref (no need to update blocks)
      lastValueRef.current = currentValue;
    }
  }, [value]);

  // Notify parent of changes when blocks change due to user interaction
  React.useEffect(() => {
    // Skip on initial mount
    if (!isMountedRef.current) {
      return;
    }

    // Skip if this is an internal update from props
    if (isInternalUpdateRef.current) {
      return;
    }

    const html = blocksToHtml(blocks);
    const normalizedHtml = normalizeHtmlForComparison(html);
    const lastValue = lastValueRef.current;

    // Only notify if HTML is different from last known value
    if (normalizedHtml !== lastValue) {
      lastValueRef.current = normalizedHtml;
      // Use the ref to avoid dependency on onChange and prevent loops
      // Call with the original HTML (not normalized) for the parent
      onChangeRef.current(html);
    }
  }, [blocks]);

  const addBlock = useCallback((type: ContentBlockType, index?: number) => {
    const newBlock: ContentBlock = {
      id: generateId(),
      type,
      content: "",
    };

    setBlocks((prev) => {
      const newBlocks = [...prev];
      if (index !== undefined) {
        newBlocks.splice(index + 1, 0, newBlock);
      } else {
        newBlocks.push(newBlock);
      }
      return newBlocks;
    });
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks((prev) => {
      const filtered = prev.filter((block) => block.id !== id);
      return filtered.length > 0
        ? filtered
        : [{ id: generateId(), type: "paragraph", content: "" }];
    });
  }, []);

  const updateBlock = useCallback(
    (id: string, updates: Partial<ContentBlock>) => {
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === id ? { ...block, ...updates } : block
        )
      );
    },
    []
  );

  const moveBlock = useCallback((id: string, direction: "up" | "down") => {
    setBlocks((prev) => {
      const index = prev.findIndex((block) => block.id === id);
      if (index === -1) return prev;
      if (direction === "up" && index === 0) return prev;
      if (direction === "down" && index === prev.length - 1) return prev;

      const newBlocks = [...prev];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      [newBlocks[index], newBlocks[newIndex]] = [
        newBlocks[newIndex],
        newBlocks[index],
      ];
      return newBlocks;
    });
  }, []);

  const getBlockIcon = (type: ContentBlockType) => {
    switch (type) {
      case "heading1":
      case "heading2":
      case "heading3":
        return type === "heading1"
          ? Heading1
          : type === "heading2"
          ? Heading2
          : Heading3;
      case "image":
        return Image;
      case "video":
        return Video;
      default:
        return Type;
    }
  };

  const getBlockLabel = (type: ContentBlockType) => {
    switch (type) {
      case "heading1":
        return "Heading 1";
      case "heading2":
        return "Heading 2";
      case "heading3":
        return "Heading 3";
      case "image":
        return "Image";
      case "video":
        return "Video";
      default:
        return "Paragraph";
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <AnimatePresence mode="popLayout">
        {blocks.map((block, index) => {
          const Icon = getBlockIcon(block.type);
          return (
            <motion.div
              key={block.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="group relative border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
            >
              {/* Block Controls */}
              <div className="absolute -left-12 top-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveBlock(block.id, "up")}
                  disabled={index === 0}
                  className="h-8 w-8 p-0"
                >
                  <MoveUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveBlock(block.id, "down")}
                  disabled={index === blocks.length - 1}
                  className="h-8 w-8 p-0"
                >
                  <MoveDown className="h-4 w-4" />
                </Button>
              </div>

              {/* Block Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Icon className="h-4 w-4" />
                  <span>{getBlockLabel(block.type)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBlock(block.id)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Block Content */}
              <div className="space-y-3">
                {block.type === "paragraph" && (
                  <textarea
                    value={block.content}
                    onChange={(e) =>
                      updateBlock(block.id, { content: e.target.value })
                    }
                    placeholder={placeholder}
                    className="w-full min-h-[100px] p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 resize-y"
                    rows={4}
                  />
                )}

                {(block.type === "heading1" ||
                  block.type === "heading2" ||
                  block.type === "heading3") && (
                  <input
                    type="text"
                    value={block.content}
                    onChange={(e) =>
                      updateBlock(block.id, { content: e.target.value })
                    }
                    placeholder={`Enter ${getBlockLabel(
                      block.type
                    ).toLowerCase()}...`}
                    className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${
                      block.type === "heading1"
                        ? "text-3xl font-bold"
                        : block.type === "heading2"
                        ? "text-2xl font-semibold"
                        : "text-xl font-medium"
                    }`}
                  />
                )}

                {block.type === "image" && (
                  <div className="space-y-3">
                    <ImageUpload
                      value={block.content}
                      onChange={(url) => {
                        const imageUrl = Array.isArray(url)
                          ? url[0] || ""
                          : url || "";
                        updateBlock(block.id, { content: imageUrl });
                      }}
                      className="w-full"
                    />
                    <input
                      type="text"
                      value={block.alt || ""}
                      onChange={(e) =>
                        updateBlock(block.id, { alt: e.target.value })
                      }
                      placeholder="Image alt text..."
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm"
                    />
                    <input
                      type="text"
                      value={block.caption || ""}
                      onChange={(e) =>
                        updateBlock(block.id, { caption: e.target.value })
                      }
                      placeholder="Image caption (optional)..."
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm"
                    />
                  </div>
                )}

                {block.type === "video" && (
                  <div className="space-y-3">
                    <input
                      type="url"
                      value={block.videoUrl || ""}
                      onChange={(e) => {
                        const url = e.target.value;
                        const source = url.includes("youtube")
                          ? "youtube"
                          : url.includes("vimeo")
                          ? "vimeo"
                          : "other";
                        updateBlock(block.id, {
                          videoUrl: url,
                          videoSource: source,
                        });
                      }}
                      placeholder="Video URL (YouTube, Vimeo, or direct link)..."
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                    {block.videoUrl && (
                      <div className="aspect-video bg-gray-100 dark:bg-gray-900 rounded-md flex items-center justify-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Video preview will appear here
                        </p>
                      </div>
                    )}
                    <input
                      type="text"
                      value={block.caption || ""}
                      onChange={(e) =>
                        updateBlock(block.id, { caption: e.target.value })
                      }
                      placeholder="Video caption (optional)..."
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Add Block Button */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock("paragraph", index)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Paragraph
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock("heading1", index)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    H1
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock("heading2", index)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    H2
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock("heading3", index)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    H3
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock("image", index)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Image
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock("video", index)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Video
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Add First Block Button */}
      {blocks.length === 0 && (
        <Button
          type="button"
          variant="outline"
          onClick={() => addBlock("paragraph")}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Content Block
        </Button>
      )}
    </div>
  );
};

export default RichTextEditor;
